/**
 * The wiring screen's earlier answers, named for the owner, and the one suggestion it may make
 * (field report F3.7). Pure, and no graph walk of its own: the page hands it the keys
 * `arrivingKeys` / `arrivingKeysOnSomePath` found.
 *
 * Why: the owner's studios list saved `studio`, the free-slots connection wanted `studio_id`, and
 * the "Which answer" list showed a bare key name she had never typed, with no hint that it was
 * the studio her customer picked. The list now says what each answer IS and which step got it,
 * and a detail whose name means the same as one answer is offered that answer first.
 */
import { writtenKeys } from './task-flow-draft.js';

/** How the step that saved an answer got it, for the words the list shows. */
export const ANSWER_KIND = Object.freeze({
  choose: 'PICKED',
  choose_live: 'PICKED',
  ask: 'GAVE',
  call: 'SYSTEM',
});

/** `studio_id`, `studioId`, `studio-id` all read `studio id`. */
export const spokenKey = key =>
  String(key ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.\s]+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Each key with what the list needs to name it: `{ value, kind, answer, step }`, in the order
 * given. `step` is the owner's own step name ('' when she has not written one).
 */
export const namedAnswers = (definition, keys) => {
  const written = writtenKeys(definition);
  const steps = new Map((definition?.steps ?? []).map(step => [step.id, step]));
  return keys.map(key => {
    const row = written.find(one => one.key === key);
    const type = steps.get(row?.stepId)?.type;
    return {
      value: key,
      kind: ANSWER_KIND[type] ?? 'GAVE',
      answer: spokenKey(key),
      step: row?.stepName ?? '',
    };
  });
};

/** Words that name an identifier rather than a thing: `studio_id` means the studio. */
const ID_WORDS = new Set([
  'id',
  'ids',
  'code',
  'key',
  'ref',
  'uuid',
  'no',
  'num',
  'number',
]);

const singular = word =>
  word.length > 3 && word.endsWith('s') && !word.endsWith('ss')
    ? word.slice(0, -1)
    : word;

/** What a name means, for matching: identifier words dropped, plurals made singular. */
export const meaningOf = name => {
  const words = spokenKey(name).split(' ').filter(Boolean);
  const core = words.filter(word => !ID_WORDS.has(word));
  return (core.length > 0 ? core : words).map(singular).join(' ');
};

/**
 * The ONE earlier answer that means what a detail's name means, or null.
 *
 * A SUGGESTION ONLY: nothing is filled in until she presses "Use it". Decision 5 fills exact
 * names alone (`WiringScreen.vue`), because a near match guessed wrong sends somebody else's
 * answer to her system and nothing on screen shows it. So an exact name is never suggested
 * (Decision 5 already filled it), and two answers with one meaning suggest neither.
 */
export const suggestFor = (detail, keys) => {
  const wanted = meaningOf(detail);
  if (wanted === '') return null;
  const hits = keys.filter(key => key !== detail && meaningOf(key) === wanted);
  return hits.length === 1 ? hits[0] : null;
};
